/**
 * FLASH — lightning, and the only family in this engine with a safety
 * requirement rather than an aesthetic one.
 *
 * ⚠ WCAG 2.3.1 (Three Flashes or Below Threshold, Level A): a page must not
 * contain anything that flashes more than three times in any one-second period,
 * unless the flash is below the general and red flash thresholds. A full-viewport
 * luminance change is well outside the small-safe-area exemption, so BOTH limits
 * below are enforced, and NEITHER is configurable by a merchant:
 *
 *   MAX_FLASHES_PER_SECOND  a hard rolling-window limiter. A merchant who drags
 *                           "frequency" to maximum gets 3/s and no more.
 *   MAX_PEAK_ALPHA          a hard ceiling on the luminance delta, so even a
 *                           permitted flash stays modest against a dark theme.
 *
 * These are not defaults with a merchant-facing override. Photosensitive
 * epilepsy is the failure mode, and "the merchant chose it" is not a defence —
 * the person harmed is the shopper, who chose nothing. It is also a listed App
 * Store review risk for an animation app.
 *
 * The shape of a real strike is also deliberate. Lightning is not a square wave:
 * it is a very fast rise, a fast decay, and usually a second smaller stroke
 * ~60 ms later. A single symmetric fade reads as a screen glitch.
 */

import { clamp } from '../budget.ts';
import type { Family, Particle, StepContext } from '../types.ts';

/** WCAG 2.3.1. Not a default — a ceiling. */
export const MAX_FLASHES_PER_SECOND = 3;

/**
 * Hard cap on the peak overlay alpha.
 *
 * A white overlay at 0.18 over a typical storefront is a clearly visible
 * lightning flash and a modest relative-luminance change. Above ~0.25 it starts
 * to approach the general flash threshold on dark themes, so the ceiling sits
 * comfortably below that rather than at it.
 */
export const MAX_PEAK_ALPHA = 0.18;

export interface FlashOptions {
  /** Seconds between strikes. Clamped so the rate limiter is a backstop, not the norm. */
  interval: number;
  /** Random +/- fraction applied to `interval`, so strikes are not metronomic. */
  jitter: number;
  /** Peak alpha of the overlay. Clamped to MAX_PEAK_ALPHA. */
  peak: number;
  /** Seconds for the flash to decay. */
  decay: number;
  /** Emit a second, smaller stroke shortly after the first. */
  doubleStrike: boolean;
  /** Overlay colour. */
  color: string;
}

interface FlashState {
  /** Times (in ctx.t seconds) at which recent flashes STARTED. */
  recent: number[];
  /** ctx.t at which the current flash began, or -1 when idle. */
  startedAt: number;
  /** ctx.t of the next scheduled strike. */
  nextAt: number;
  /** Whether the second stroke of the current strike is still pending. */
  secondPending: boolean;
}

/**
 * Would starting a flash now exceed WCAG 2.3.1?
 *
 * Exported and pure so the limiter can be tested directly rather than inferred
 * from rendered output. A safety limit that is only tested through a canvas is
 * a safety limit nobody can prove.
 */
export function flashAllowed(recent: number[], now: number): boolean {
  // Count flashes that started within the trailing one-second window.
  let n = 0;
  for (let i = recent.length - 1; i >= 0; i--) {
    if (now - recent[i] < 1) n++;
    else break; // `recent` is ascending, so we can stop
  }
  return n < MAX_FLASHES_PER_SECOND;
}

export function createFlashFamily(opts: FlashOptions, rnd: () => number = Math.random): Family {
  const peak = clamp(opts.peak, 0, MAX_PEAK_ALPHA);
  // Even at the fastest merchant setting, never schedule faster than the limit
  // allows. The limiter still runs — this just stops it from being the thing
  // that shapes the effect.
  const interval = Math.max(1 / MAX_FLASHES_PER_SECOND, opts.interval);
  const decay = Math.max(0.05, opts.decay);

  const s: FlashState = { recent: [], startedAt: -1, nextAt: -1, secondPending: false };

  return {
    id: 'flash',
    // A lightning flash is a luminance change, not a sprite. Zero particles.
    countScale: 0,

    spawn() {
      /* no particles */
    },
    step(_p: Particle): boolean {
      return true;
    },

    overlay(g: CanvasRenderingContext2D, ctx: StepContext) {
      if (s.nextAt < 0) s.nextAt = ctx.t + interval * (0.4 + rnd() * 0.6);

      // ── schedule ──────────────────────────────────────────────────────────
      if (s.startedAt < 0 && ctx.t >= s.nextAt) {
        if (flashAllowed(s.recent, ctx.t)) {
          s.startedAt = ctx.t;
          s.secondPending = opts.doubleStrike;
          s.recent.push(ctx.t);
          // Keep the window small; anything older than a second cannot matter.
          while (s.recent.length && ctx.t - s.recent[0] > 1) s.recent.shift();
        }
        // Whether or not the strike was permitted, schedule the next one. A
        // refused strike is simply skipped — it is never queued up to fire in a
        // burst the moment the window clears, which would defeat the limiter.
        s.nextAt = ctx.t + interval * (1 - opts.jitter + rnd() * opts.jitter * 2);
      }

      if (s.startedAt < 0) return;

      const age = ctx.t - s.startedAt;

      // ── the second stroke ─────────────────────────────────────────────────
      // A real strike often has a smaller return stroke ~60 ms later. It shares
      // the parent strike's slot in the rate limiter rather than taking its own,
      // because perceptually it is one event — and because counting it
      // separately would halve the permitted strike rate for no safety gain.
      let a: number;
      if (s.secondPending && age > 0.06 && age < 0.06 + decay) {
        a = peak * 0.55 * (1 - (age - 0.06) / decay);
      } else {
        // Fast rise (~12 ms), then an exponential-ish decay.
        a = age < 0.012 ? peak * (age / 0.012) : peak * Math.pow(1 - clamp(age / decay, 0, 1), 2);
      }

      if (age > decay + (opts.doubleStrike ? 0.06 : 0)) {
        s.startedAt = -1;
        s.secondPending = false;
        return;
      }

      if (a <= 0.002) return;
      g.globalAlpha = clamp(a, 0, MAX_PEAK_ALPHA);
      g.fillStyle = opts.color;
      g.fillRect(0, 0, ctx.w, ctx.h);
    },
  };
}

export const FLASH_PRESETS = {
  /** A distant storm: infrequent, soft, single stroke. */
  distant: {
    interval: 7,
    jitter: 0.5,
    peak: 0.09,
    decay: 0.42,
    doubleStrike: false,
    color: '#DCE9FF',
  },
  /** Overhead: frequent, bright, double stroke. */
  storm: {
    interval: 3.5,
    jitter: 0.45,
    peak: 0.16,
    decay: 0.3,
    doubleStrike: true,
    color: '#FFFFFF',
  },
  /** A warm camera-flash pop for a sale moment. */
  paparazzi: {
    interval: 2.2,
    jitter: 0.6,
    peak: 0.12,
    decay: 0.16,
    doubleStrike: false,
    color: '#FFF6D8',
  },
} as const satisfies Record<string, FlashOptions>;

export type FlashPresetId = keyof typeof FLASH_PRESETS;

export function flashOptions(preset: FlashPresetId): FlashOptions {
  return { ...FLASH_PRESETS[preset] };
}
